import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import RegulationsModel from "slotworks/model/regulations/regulations-model";
import SmartUIFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import HeadlessSmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import StateMachine from "slotworks/state-machine/state-machine";
import { AddWindowFocusChangeListener, RemoveWindowFocusChangeListener } from "slotworks/utils/browser-utils";
import { Keyboard } from "slotworks/utils/keyboard";
import Timer from "slotworks/utils/timer";
import { AbstractControl } from "../control";

export class HoldSpinControl extends AbstractControl {

    public static SPACE_TO_SKIP: boolean = true;
    public static ENTER_TO_SKIP: boolean = false;

    public static startHoldTime: number = 1000;
    public static skipIntervalDuration: number = 100;

    public static isHeld: boolean = false;

    public static cancel() {
        HoldSpinControl.isHeld = false;
        Timer.clearInterval(HoldSpinControl.skipIntervalId);
        if (HoldSpinControl.activeInstance) {
            HoldSpinControl.activeInstance.stopSkipping();
        }
    }

    protected static expiredKeyEventListeners: Array<{ event: string, callback: EventListener }> = [];
    protected static skipIntervalId: number = -1;
    protected static startTimeoutId: number = -1;
    protected static activeInstance: HoldSpinControl;

    protected spinButton: FlexiButton;
    protected skipButton: FlexiButton;
    protected overlayButton: PIXI.Graphics;

    protected animating: boolean;

    public destroy(): void {
        HoldSpinControl.activeInstance = null;
        this.stopSkipping();

        if (this.spinButton) {
            this.spinButton.destroy();
        }
        if (this.skipButton) {
            this.skipButton.destroy();
        }
        if (this.overlayButton) {
            this.overlayButton.removeAllListeners();
            this.overlayButton = null;
        }

        SmartUIFlags.onFlagsUpdated.remove(this.smartFlagsUpdated);

        document.body.removeEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), this.onButtonOut, true);
        document.body.removeEventListener(ButtonEvent.POINTER_OUT.getDOMEventString(), this.onButtonOut, true);
        RemoveWindowFocusChangeListener(this.onButtonOut);
        this.removeExpiredEventListeners();
        this.removeAnimatedButtonListeners();

        super.destroy();
    }

    public setup(
        name: string,
        layer: Layers,
        assetName: string,
        assetNames: string[],
        smartUIGroup: HeadlessSmartUIGroup
    ): void {
        super.setup(name, layer, assetName, assetNames, smartUIGroup);

        HoldSpinControl.activeInstance = this;

        this.addOrRemoveKeyListeners({ keydown: true, keyup: true });

        this.spinButton = layer.getFlexiButton(assetName);

        this.skipButton = layer.getFlexiButton(assetName + "_skip");
        if (this.skipButton) {
            this.addButtonListeners(this.skipButton);
        }

        this.overlayButton = layer.getShape(assetName + "_overlay");
        if (this.overlayButton) {
            this.overlayButton.interactive = true;
            this.overlayButton.addListener(ButtonEvent.CLICK.getPIXIEventString() as any, () => {
                let turboSpin = false;
                if (this.options) {
                    turboSpin = this.options.turboSpin;
                }
                const enabled = this.enabled && RegulationsModel.skipAllowed && !turboSpin;
                const skipping = this.gameIsNoskip();
                if (enabled && !skipping && !this.gameIsIdle()) {
                    this.onButtonUp();
                }
            });
        }

        SmartUIFlags.onFlagsUpdated.add(this.smartFlagsUpdated);

        this.showButton();

        document.body.addEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), this.onButtonOut, true);
        document.body.addEventListener(ButtonEvent.POINTER_OUT.getDOMEventString(), this.onButtonOut, true);
        AddWindowFocusChangeListener(this.onButtonOut);
        this.addButtonListeners(this.spinButton);
        this.addAnimatedButtonListeners();
    }

    protected addButtonListeners(button: FlexiButton) {
        window.addEventListener("mouseout", this.onButtonOut);

        button.on(ButtonEvent.POINTER_DOWN, this.onButtonDown);
        button.on(ButtonEvent.POINTER_UP, this.onButtonUp);
        button.on(ButtonEvent.POINTER_OUT, this.onButtonOut);
        button.on(ButtonEvent.POINTER_UP_OUTSIDE, this.onButtonOut);
    }

    protected addAnimatedButtonListeners() {
        const holdSpinIntro = this.layer.getAnimatedSprite("spin_hold_intro");
        const holdSpinLoop = this.layer.getAnimatedSprite("spin_hold_loop");

        if (holdSpinIntro) {
            holdSpinIntro.on(ButtonEvent.POINTER_OUT.getPIXIEventString(), this.onButtonOut);
            holdSpinIntro.on(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString(), this.onButtonOut);
        }

        if (holdSpinLoop) {
            holdSpinLoop.on(ButtonEvent.POINTER_OUT.getPIXIEventString(), this.onButtonOut);
            holdSpinLoop.on(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString(), this.onButtonOut);
        }
    }

    protected removeAnimatedButtonListeners() {
        const holdSpinIntro = this.layer.getAnimatedSprite("spin_hold_intro");
        const holdSpinLoop = this.layer.getAnimatedSprite("spin_hold_loop");

        if (holdSpinIntro) {
            holdSpinIntro.off(ButtonEvent.POINTER_OUT.getPIXIEventString(), this.onButtonOut);
            holdSpinIntro.off(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString(), this.onButtonOut);
        }

        if (holdSpinLoop) {
            holdSpinLoop.off(ButtonEvent.POINTER_OUT.getPIXIEventString(), this.onButtonOut);
            holdSpinLoop.off(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString(), this.onButtonOut);
        }
    }

    protected addOrRemoveKeyListeners(status: { keydown: boolean, keyup: boolean }): void {
        this.removeExpiredEventListeners();

        if (status.keydown) {
            HoldSpinControl.expiredKeyEventListeners.push({ event: "keydown", callback: this.onKeyDown });
            document.addEventListener("keydown", this.onKeyDown);
        }

        if (status.keyup) {
            HoldSpinControl.expiredKeyEventListeners.push({ event: "keyup", callback: this.onKeyUp });
            document.addEventListener("keyup", this.onKeyUp);
        }
    }

    protected smartFlagsUpdated = (): void => {
        if (!this.animating) {
            this.showButton();
        }

        if (this.overlayButton) {
            if (
                SmartUIFlags.has(SmartUIFlag.GAMBLE_UI) ||
                SmartUIFlags.has(SmartUIFlag.PROMPT) ||
                SmartUIFlags.has(SmartUIFlag.MENU)
            ) {
                this.overlayButton.visible = false;
            } else {
                this.overlayButton.visible = true;
            }
        }
    }

    protected onButtonDown = (): void => {
        if (this.enabled && !HoldSpinControl.isHeld) {
            this.startSkipBeginTimer();
        }
    }

    protected onButtonOut = (): void => {
        this.stopSkipping();
    }

    protected onButtonUp = (): void => {
        if (this.enabled && !HoldSpinControl.isHeld) {
            if (!this.animating) {
                this.animateSpinToSkip();
            } else {
                this.stopSpinToSkip();
            }
            this.skipState();
        }
        this.stopSkipping();
    }

    protected onKeyDown = (event: KeyboardEvent): void => {
        if (
            this.checkSkipKey(event) &&
            this.spaceBarCanSpin() &&
            !HoldSpinControl.isHeld
        ) {
            this.startSkipBeginTimer();
            this.addOrRemoveKeyListeners({ keydown: false, keyup: true });
        }
    }

    protected onKeyUp = (event: KeyboardEvent): void => {
        if (this.checkSkipKey(event)) {
            if (this.spaceBarCanSpin() && !HoldSpinControl.isHeld) {
                this.animateSpinToSkip();
                this.skipState();
            }
            this.stopSkipping();
            this.addOrRemoveKeyListeners({ keydown: true, keyup: true });
        }
    }

    protected removeExpiredEventListeners(): void {
        for (const { event, callback } of HoldSpinControl.expiredKeyEventListeners) {
            document.removeEventListener(event, callback);
        }
        HoldSpinControl.expiredKeyEventListeners = [];
    }

    protected skipState(): void {
        StateMachine.skip();
    }

    protected spaceBarCanSpin(): boolean {
        return this.enabled && this.options && this.options.spaceToSpin;
    }

    protected startSkipBeginTimer() {
        HoldSpinControl.startTimeoutId = Timer.setTimeout(() => this.startSkipping(), HoldSpinControl.startHoldTime);
    }

    protected startSkipping(): void {
        this.stopSkipping();

        if (RegulationsModel.isHoldToSpinAllowed()) {
            HoldSpinControl.skipIntervalId = Timer.setInterval(() => {
                if (this.enabled) {
                    if (!HoldSpinControl.isHeld) {
                        HoldSpinControl.isHeld = true;
                        this.animateHoldSpin();
                    }
                    this.skipState();
                }
            }, HoldSpinControl.skipIntervalDuration);
        }
    }

    protected stopSkipping(): void {
        HoldSpinControl.isHeld = false;
        this.cancelHoldAnimations();
        Timer.clearInterval(HoldSpinControl.skipIntervalId);
        Timer.clearInterval(HoldSpinControl.startTimeoutId);
    }

    protected animateSpinToSkip() {
        const spinToStop = this.layer.getAnimatedSprite("spin_to_skip");
        if (spinToStop && this.isSpinToSkipAnimationAllowed()) {
            this.animating = true;

            spinToStop.visible = true;
            spinToStop.gotoAndPlay();

            spinToStop.onComplete.addOnce(() => this.stopSpinToSkip());

            this.alphaButtons(0);
            this.showButton();
        }
    }

    protected stopSpinToSkip() {
        const spinToStop = this.layer.getAnimatedSprite("spin_to_skip");

        spinToStop.onComplete.removeAll();

        this.animating = false;

        spinToStop.stop();
        spinToStop.visible = false;

        this.alphaButtons(1);
    }

    protected animateHoldSpin() {
        const holdSpinIntro = this.layer.getAnimatedSprite("spin_hold_intro");
        if (holdSpinIntro) {
            this.animating = true;

            holdSpinIntro.visible = true;
            holdSpinIntro.gotoAndPlay();
            holdSpinIntro.onComplete.addOnce(() => this.animateHoldSpinLoop());

            this.hideButtons();
        }
    }

    protected animateHoldSpinLoop() {
        const holdSpinIntro = this.layer.getAnimatedSprite("spin_hold_intro");
        const holdSpinLoop = this.layer.getAnimatedSprite("spin_hold_loop");

        holdSpinIntro.visible = false;
        holdSpinIntro.stop();

        holdSpinLoop.visible = true;
        holdSpinLoop.loop = true;
        holdSpinLoop.gotoAndPlay();
    }

    protected cancelHoldAnimations() {
        const holdSpinIntro = this.layer.getAnimatedSprite("spin_hold_intro");
        const holdSpinLoop = this.layer.getAnimatedSprite("spin_hold_loop");

        if (holdSpinIntro) {
            this.animating = false;

            holdSpinIntro.onComplete.removeAll();
            holdSpinIntro.stop();
            holdSpinIntro.visible = false;

            holdSpinLoop.onComplete.removeAll();
            holdSpinLoop.stop();
            holdSpinLoop.visible = false;

            this.showButton();
        }
    }

    protected hideButtons() {
        if (this.spinButton) {
            this.spinButton.setEnabled(false);
            this.spinButton.setVisible(false);
        }

        if (this.skipButton) {
            this.skipButton.setEnabled(false);
            this.skipButton.setVisible(false);
        }
    }

    // Used for when you want to hide buttons but still allow clicks
    protected alphaButtons(alpha: number) {
        if (this.spinButton) {
            this.spinButton.setAlpha(alpha);
        }

        if (this.skipButton) {
            this.skipButton.setAlpha(alpha);
        }

    }

    protected showButton() {
        this.hideButtons();

        let turboSpin = false;
        if (this.options) {
            turboSpin = this.options.turboSpin;
        }

        if (this.skipButton && !this.gameIsIdle()) {
            this.skipButton.setVisible(true);
            const enabled = this.enabled && RegulationsModel.skipAllowed && !turboSpin;
            const skipping = this.gameIsNoskip();

            this.skipButton.setEnabled(enabled && !skipping);
        } else {
            this.spinButton.setVisible(true);
            this.spinButton.setEnabled(this.enabled);
        }
    }

    protected checkSkipKey(event: KeyboardEvent) {
        return (event.keyCode === Keyboard.SPACE && HoldSpinControl.SPACE_TO_SKIP) || (event.keyCode === Keyboard.ENTER && HoldSpinControl.ENTER_TO_SKIP);
    }

    private gameIsIdle() {
        return SmartUIFlags.has(SmartUIFlag.IDLE);
    }

    private gameIsNoskip() {
        return SmartUIFlags.has(SmartUIFlag.NO_SKIP);
    }

    private isSpinToSkipAnimationAllowed() {
        return !this.animating && this.gameIsIdle() && RegulationsModel.skipAllowed && !this.options.turboSpin;
    }
}
